import Badge from '@axa-japan/design-system-react/Badge';
import Button from '@axa-japan/design-system-react/Button';
import Checkbox from '@axa-japan/design-system-react/CheckboxGroupField';
import Heading from '@axa-japan/design-system-react/Heading';
import { ArrowIcon } from '@axa-japan/design-system-react/Icons';
import Text from '@axa-japan/design-system-react/Text';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import tw from 'tailwind-styled-components';
import { z } from 'zod';
import ErrorMessage from '../components/ErrorMessage';
import PdfLink from '../components/PdfLink';
import useGetConnectionType from '../hooks/useGetConnectionType';
import { DOCUMENT_LIST_URL_EXTERNAL, DOCUMENT_LIST_URL_INTERNAL } from '../constants/manual';
import { AUTH_TYPE } from '../types/auth';
import { ValueOf } from '../utils/fileUtils';
import { renderCustomTags } from '../utils/renderTags';

type CheckboxProps = { checked: boolean };

export const CheckboxContainer = tw.div<CheckboxProps>`
  my-1
  flex
  w-full
  flex-col
  rounded-sm
  ${({ checked }) => (checked ? 'bg-interactiveHoverGrey' : 'bg-axaRed bg-opacity-10')}
  [&>div>div>div>label]:!flex
  [&>div>div>div>label]:!w-full
  [&>div>div>div]:!flex
  [&>div>div>div]:!w-full
  [&>div>div]:!flex
  [&>div>div]:!w-full
`;

const agreementIds = ['handlerConfirmation', 'handlerPledge'] as const;

type ArgumentId = (typeof agreementIds)[number];

type Contents = {
  [connectionType: ValueOf<typeof AUTH_TYPE>]: (string | [string, string[]])[];
};

type Agreement = { id: ArgumentId; title: string; items: Contents; checkboxLabel: string; subTitle: string };

type AgreementPageForm = {
  handlerConfirmation: boolean;
  handlerPledge: boolean;
};

type AgreementPageProps = {
  initialData: AgreementPageForm;
  onNext: (data: AgreementPageForm) => void;
};

const AgreementPageSchema = z.object({
  handlerConfirmation: z.boolean().refine((value) => value === true, 'チェックしてください。'),
  handlerPledge: z.boolean().refine((value) => value === true, 'チェックしてください。'),
});

const agreements: Agreement[] = [
  {
    id: 'handlerConfirmation',
    title: '確認事項',
    items: {
      [AUTH_TYPE.Internal]: [
        '撮影、送信する書類は新契約（契約部扱）書類であること',
        [
          '送信後、新契約査定担当者は即時に画像を確認できないため、以下にご注意いただくこと',
          [
            'アップロードが集中した場合など翌営業日の対応になる場合があること',
            '口座振替依頼書は原則として15時までにアップロードされた分が翌営業日の対応になること',
          ],
        ],
        '新契約査定が終わってからの提出はできないこと（契約部から特に求められた場合を除く）',
        '撮影した画像が不鮮明である場合など、改めての撮影、または書面の提出等が必要になるケースがあること',
        '取扱者のタブレット等に書類画像を保存しないこと（一時的に保存した場合は送信後、直ちに完全に削除すること）',
        '書類の原本の取扱は書類によって異なることに注意すること',
      ],
      [AUTH_TYPE.External]: [
        '撮影、送信する書類は新契約用の以下の書類であること<br/><bold>「健康診断結果通知書」「人間ドック成績表」「弔慰金規定または福利厚生規定」「保険契約締結に関する権限委任の届出書」</bold> <br/>※上記以外の書類は不備対応、後送においては撮影・送信可能',
        'お客様の書類である「健康診断結果通知書」「人間ドック成績表」「弔慰金規定または福利厚生規定」は、送信後に書類の原本の提出は不要ですが、<bold>これら以外の書類は送信後も書面の提出が必要</bold>であること',
        [
          '送信後、<red>新契約査定担当者は即時に画像を確認できない</red>ため、以下にご注意いただくこと',
          [
            'アップロードが集中した場合など<red>翌営業日の対応になる場合がある</red>こと',
            '口座振替依頼書は原則として15時までにアップロードされた分が翌営業日の対応になること',
          ],
        ],
        '新契約査定が終わってからの提出はできないこと（新契約査定担当者から特に求められた場合を除く）',
        '撮影した画像が不鮮明である場合など、改めての撮影、または書面の提出等が必要になるケースがあること',
        '撮影はお客様のスマートフォンを利用すること（取扱者のスマートフォンでの撮影は禁止）',
      ],
    },
    checkboxLabel: '私は、上記の各事項を確認しました',
    subTitle: '',
  },
  {
    id: 'handlerPledge',
    title: '誓約事項',
    items: {
      [AUTH_TYPE.Internal]: [
        'お客様の個人情報の取り扱いに充分留意すること',
        '書類受付番号を正しく入力すること',
        '健診結果通知書等、お客様の書類を撮影、送信する場合は、原本と内容に相違が無いことをお客様に確認いただくこと',
        'マスキングが必要な書類（健康診断結果通知書、本人確認書類等）では該当箇所に付箋を貼る等を行ったうえで撮影すること',
      ],
      [AUTH_TYPE.External]: [
        'お客様の個人情報の取り扱いに充分留意すること',
        '書類受付番号を正しく入力すること',
        '健診結果通知書等、お客様の書類を撮影、送信する場合は、原本と内容に相違が無いことをお客様に確認いただくこと',
        'マスキングが必要な書類（健康診断結果通知書、本人確認書類等）では該当箇所に付箋を貼る等を行ったうえで撮影すること（マスキングが必要な書類を選択すると具体的なマスキング項目が表示されます）',
      ],
    },

    checkboxLabel: '私は、上記の各事項を遵守することを誓約します',
    subTitle: '',
  },
];

const AgreementPage: React.FC<AgreementPageProps> = ({ onNext }) => {
  const connectionType = useGetConnectionType();

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<AgreementPageForm>({
    resolver: zodResolver(AgreementPageSchema),
    mode: 'onSubmit',
    defaultValues: {
      handlerConfirmation: false,
      handlerPledge: false,
    },
  });

  const onSubmit: SubmitHandler<AgreementPageForm> = (formData) => {
    onNext(formData);
  };

  return (
    <>
      <div>
        <Heading as="h1" className="pb-4">
          ご利用上の注意・誓約
        </Heading>
        <Text>このシステムでは、新契約（契約部扱）に関する書類をカメラ撮影、送信することができます。</Text>
        <Text>下記の確認事項、誓約事項をご確認のうえ、ご使用ください。</Text>
        <PdfLink
          label="取扱い可能な書類と推奨動作環境"
          href={connectionType === AUTH_TYPE.Internal ? DOCUMENT_LIST_URL_INTERNAL : DOCUMENT_LIST_URL_EXTERNAL}
        />
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-10">
          {agreements.map(({ id, title, items, checkboxLabel, subTitle }) => {
            const contents = items[connectionType || AUTH_TYPE.External];

            return (
              <div key={id}>
                <div className="flex mb-3 items-center gap-5">
                  <Heading as="h2">{title}</Heading>
                  <Badge variant="important" text="必須" />
                </div>
                {subTitle && <Text>{subTitle}</Text>}
                <div className="px-6">
                  <ol className="list-decimal">
                    {contents?.map((agreement, index) => (
                      <li key={index}>
                        {Array.isArray(agreement) ? (
                          <>
                            {renderCustomTags(agreement[0])}
                            <ul className="list-disc ml-6">
                              {agreement[1].map((text, idx) => (
                                <li key={idx}>{renderCustomTags(text)}</li>
                              ))}
                            </ul>
                          </>
                        ) : (
                          renderCustomTags(agreement)
                        )}
                      </li>
                    ))}
                  </ol>
                </div>

                <CheckboxContainer checked={watch(id)}>
                  <Controller
                    name={id}
                    control={control}
                    defaultValue={false}
                    render={({ field }) => (
                      <Checkbox
                        showError={Boolean(errors[id])}
                        value={watch(id) ? [id] : []}
                        required={true}
                        onChange={field.onChange}
                        options={[
                          {
                            defaultChecked: field.value,
                            label: checkboxLabel,
                            value: id,
                          },
                        ]}
                      />
                    )}
                  />
                </CheckboxContainer>
                {errors[id] && <ErrorMessage message={String(errors[id]?.message)} />}
              </div>
            );
          })}

          <div className="flex justify-center gap-3 mt-5">
            <div className="w-80">
              <Button type="submit" fullWidth icon={<ArrowIcon />}>
                書類選択画面へ
              </Button>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default AgreementPage;
